import { createContext, useContext, useEffect, useState } from "react";
import { Navigate, Outlet, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Newspaper, Users, Shield, Trophy, Calendar, Video, ListTree, Image, ScrollText, LogOut, PlusCircle, Mail } from "lucide-react";
import { http, errorText } from "@/lib/api";
import { toast } from "sonner";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  useEffect(() => {
    if (!localStorage.getItem("ms_token")) { setUser(false); return; }
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);
  const login = async (email, password) => {
    const { data } = await http.post("/auth/login", { email, password });
    localStorage.setItem("ms_token", data.access_token);
    setUser(data.user);
  };
  const logout = async () => { await http.post("/auth/logout").catch(() => {}); localStorage.removeItem("ms_token"); setUser(false); };
  return <AuthCtx.Provider value={{ user, login, logout }}>{children}</AuthCtx.Provider>;
}

export function AdminLogin() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  if (user) return <Navigate to="/admin" replace />;
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await login(email, password); nav("/admin"); } catch (ex) { setErr(errorText(ex.response?.data?.detail) || ex.message); } finally { setBusy(false); }
  };
  return (
    <div className="grid min-h-[80vh] place-items-center bg-slate-50 px-4" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-slate-100 bg-white p-8 shadow-xl">
        <img src="/logo.png" alt="MiniSports" className="mx-auto h-14" />
        <h1 className="mt-4 text-center font-display text-3xl font-extrabold">Admin Portal</h1>
        <label className="mt-6 block text-xs font-bold uppercase tracking-wider text-slate-500">Email</label>
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" className="mt-1 h-11 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-[#047857]" />
        <label className="mt-4 block text-xs font-bold uppercase tracking-wider text-slate-500">Password</label>
        <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" className="mt-1 h-11 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-[#047857]" />
        {err && <p className="mt-3 text-sm text-red-600" data-testid="admin-login-error">{err}</p>}
        <button disabled={busy} data-testid="admin-login-submit" className="btn-green mt-6 w-full justify-center py-3">{busy ? "Signing in…" : "Sign In"}</button>
      </form>
    </div>
  );
}

const LINKS = [
  ["Dashboard", "/admin", LayoutDashboard], ["Add Story", "/admin/new-story", PlusCircle], ["Articles", "/admin/news", Newspaper], ["Athletes", "/admin/athletes", Users],
  ["Teams", "/admin/teams", Shield], ["Competitions", "/admin/competitions", Trophy], ["Fixtures", "/admin/fixtures", Calendar], ["Videos", "/admin/videos", Video],
  ["Sports", "/admin/sports", ListTree], ["Banners", "/admin/banners", Image], ["Subscribers", "/admin/subscribers", Mail], ["Source Logs", "/admin/logs", ScrollText],
];

export function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  if (user === null) return <div className="p-10 text-center text-slate-500">Checking session…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return (
    <div className="grid min-h-screen lg:grid-cols-[240px_1fr]" data-testid="admin-layout">
      <aside className="bg-[#0B1120] p-5 text-white">
        <img src="/logo.png" alt="" className="h-10 rounded bg-white p-1" />
        <p className="mt-3 text-xs text-slate-400">Signed in as <b className="text-white">{user.email}</b></p>
        <nav className="mt-6 grid grid-cols-2 gap-1 lg:grid-cols-1">
          {LINKS.map(([l, to, I]) => <NavLink key={to} to={to} end={to === "/admin"} data-testid={`admin-nav-${l.toLowerCase().replace(" ", "-")}`} className={({ isActive }) => `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold ${isActive ? "bg-[#047857] text-white" : "text-slate-300 hover:bg-white/10"}`}><I size={16} /> {l}</NavLink>)}
        </nav>
        <button onClick={async () => { await logout(); nav("/admin/login"); toast("Signed out"); }} data-testid="admin-logout-btn" className="mt-6 flex items-center gap-2 text-sm text-slate-400 hover:text-white"><LogOut size={16} /> Sign out</button>
      </aside>
      <main className="bg-slate-50 p-6 lg:p-8"><Outlet /></main>
    </div>
  );
}
